import { useDialogFocus } from "hooks/useDialogFocus";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import LineIcon from "./LineIcon";

import styles from "./ImageLightbox.module.css";

export type LightboxImage = {
	url: string;
	name?: string | null;
};

type ImageLightboxProps = {
	images: LightboxImage[];
	index: number;
	title: string;
	onIndexChange: (index: number) => void;
	onClose: () => void;
};

/**
 * A dependency-free, full-screen image viewer. Click the backdrop or press Esc to close; navigate
 * with the arrow buttons or the Left/Right keys. Purely a UI treatment — no external lightbox lib.
 */
const ImageLightbox = ({ images, index, title, onIndexChange, onClose }: ImageLightboxProps) => {
	const { t } = useTranslation("homePage");
	const dialogRef = useDialogFocus<HTMLDivElement>();
	const total = images.length;
	const hasMultiple = total > 1;

	const goPrev = useCallback((): void => {
		onIndexChange((index - 1 + total) % total);
	}, [index, onIndexChange, total]);

	const goNext = useCallback((): void => {
		onIndexChange((index + 1) % total);
	}, [index, onIndexChange, total]);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.key === "Escape") {
				onClose();
			} else if (event.key === "ArrowLeft" && hasMultiple) {
				goPrev();
			} else if (event.key === "ArrowRight" && hasMultiple) {
				goNext();
			}
		};

		document.addEventListener("keydown", onKeyDown);

		return () => document.removeEventListener("keydown", onKeyDown);
	}, [goNext, goPrev, hasMultiple, onClose]);

	const current = images[index];
	if (!current) {
		return null;
	}

	return (
		<div
			ref={dialogRef}
			aria-label={t("editor.lightbox.label", { title })}
			aria-modal="true"
			className={styles.lightboxOverlay}
			role="dialog">
			{/* Full-bleed backdrop is its own button so the image/controls stay non-interactive. */}
			<button
				aria-label={t("editor.lightbox.close")}
				className={styles.lightboxBackdrop}
				onClick={onClose}
				type="button"
			/>

			<button
				aria-label={t("editor.lightbox.close")}
				className={styles.lightboxClose}
				onClick={onClose}
				type="button">
				<LineIcon name="close" />
			</button>

			{hasMultiple && (
				<button
					aria-label={t("editor.lightbox.previous")}
					className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
					onClick={goPrev}
					type="button">
					<LineIcon name="chevronLeft" />
				</button>
			)}

			<figure className={styles.lightboxFigure}>
				<img alt={title} className={styles.lightboxImage} src={current.url} />
				{hasMultiple && (
					<figcaption className={styles.lightboxCounter}>
						{t("editor.lightbox.counter", { current: index + 1, total })}
					</figcaption>
				)}
			</figure>

			{hasMultiple && (
				<button
					aria-label={t("editor.lightbox.next")}
					className={`${styles.lightboxNav} ${styles.lightboxNext}`}
					onClick={goNext}
					type="button">
					<LineIcon name="chevronRight" />
				</button>
			)}
		</div>
	);
};

export default ImageLightbox;
